import { SortOrderType } from '@repo/common';
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDownIcon, Edit, Loader2Icon, Trash } from 'lucide-react';
import { LuArrowUpDown } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { ButtonGroup } from '@components/ui/button';

import { Route } from '@/constants';
import { TSupplierListItem } from '@/features/slice/suppliers/supplier-api.schema';
import { useUrlFilter } from '@/hooks';
import { useSupplierList } from '@/modules/suppliers/supplier-list/supplier-list.hook';

const ActionCell = ({ row }: CellContext<TSupplierListItem, unknown>) => {
    const { handleDeleteSupplier, isDeleting, handleEditSupplier } =
        useSupplierList();
    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                <Link to={`${Route.SUPPLIERS}/${row.original.id}`}>
                    <Button type={'button'} variant={'outline'}>
                        View Details
                    </Button>
                </Link>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className={'flex gap-2'}
                                onClick={() =>
                                    handleEditSupplier(row.original.id)
                                }
                            >
                                <Edit className={'size-4'} />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeleteSupplier(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin'} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

const NameHeader = () => {
    const { sortOrder, setSortOrder, setSortBy, sortBy } = useUrlFilter();

    const handleToggleSort = () => {
        if (sortOrder === SortOrderType.ASC) {
            setSortOrder(SortOrderType.DESC);
        } else if (sortOrder === SortOrderType.DESC) {
            setSortOrder(SortOrderType.ASC);
        }

        if (sortBy !== 'name') {
            setSortBy('name');
        }
    };

    return (
        <Button variant="ghost" onClick={handleToggleSort}>
            Supplier Name
            <LuArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
    );
};

export const supplierListColumns: ColumnDef<TSupplierListItem>[] = [
    {
        accessorKey: 'name',
        header: NameHeader,
        cell: ({ row }) => {
            const name = row.original.name;

            return (
                <div className="max-w-[180px] truncate" title={name}>
                    <Link
                        to={`${Route.SUPPLIERS}/${row.original.id}`}
                        className="font-medium hover:cursor-pointer hover:underline"
                    >
                        {name}
                    </Link>
                </div>
            );
        },
    },

    {
        accessorKey: 'email',
        header: () => <div>Email</div>,
        cell: ({ row }) => {
            const email = row.original.email || '—';

            return (
                <div className="max-w-[220px] truncate" title={email}>
                    {email}
                </div>
            );
        },
    },
    {
        accessorKey: 'phone_number',
        header: () => <div>Phone Number</div>,
        cell: ({ row }) => <div>{row.original.phone_number || '—'}</div>,
    },
    {
        accessorKey: 'address',
        header: () => <div>Address</div>,
        cell: ({ row }) => {
            const address = row.original.address || '—';

            return (
                <div className="max-w-[240px] truncate" title={address}>
                    {address}
                </div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className={'text-right'}>Actions</div>,
        cell: ActionCell,
    },
];
